<template>
    <b-container fluid>
        <br>

        <div id="projectSelector"
             v-if="accountRole.some(role => ['ROLE_ADMIN', 'ROLE_DEVELOPER'].includes(role))">
            <h2>Project selection</h2>
            <hr>
            <div class="flexRow">
                <div class="col-md-4 col-xl-3 col-6">
                    <h6>Projects</h6>
                    <v-skeleton-loader
                            :loading="this.loadingProjects"
                            transition='fade-transition'
                            ref="skeleton"
                            type="list-item">
                        <b-form-select v-model="selectedProject" :options="projectOptions"/>
                    </v-skeleton-loader>
                </div>
                <v-spacer/>
                <div class="col-md-3 col-6">
                    <h6>Update dashboard</h6>
                    <v-btn :style="primaryStyle" @click="updateAllCharts">Update
                    </v-btn>
                </div>
            </div>
        </div>

        <br>

        <div class="infoBox">
            <h2>Monthly statistics</h2>
            <v-spacer/>
            <v-btn large icon @click="this.updateIndexDisplay" :loading="this.loadingStatisticsCircle">
                <v-icon large :color="sitionColor">mdi-sync</v-icon>
                <template v-slot:loader>
                    <span class="custom-loader">
                        <v-icon :color="sitionColor">mdi-sync</v-icon>
                    </span>
                </template>
            </v-btn>
        </div>
        <hr>

        <div id="quickStats" class="infoBox flexRow">
            <div class="col-12 col-md-7 col-xl-8 flexRow" style="justify-content: space-evenly">
                <div id="queuedStats" class="col-sm-6 col-md-6 col-lg-4 col-xl-2">
                    <div class="card infoCard">
                        <div class="card-header">
                            <span class="mdi mdi-36px mdi-calendar-clock"/>
                        </div>
                        <div class="card-body infoCardBody">
                            <v-skeleton-loader
                                    :loading="this.loadingStatistics"
                                    transition='scale-transition'
                                    ref="skeleton"
                                    type="image"
                                    class="mx-auto"
                                    height="48px">
                                <h2 class="infoCardText">{{this.data.tasks_info.queued_tasks}}</h2>
                            </v-skeleton-loader>
                            <p class="infoCardText">Queued tasks</p>
                        </div>
                    </div>
                </div>
                <div id="processingStats" class="col-sm-6 col-md-6 col-lg-4 col-xl-2">
                    <div class="card infoCard">
                        <div class="card-header"><span class="mdi mdi-36px mdi-cog-clockwise"/></div>
                        <div class="card-body infoCardBody">
                            <v-skeleton-loader
                                    :loading="this.loadingStatistics"
                                    transition='scale-transition'
                                    ref="skeleton"
                                    type="image"
                                    class="mx-auto"
                                    height="48px">
                                <h2 class="infoCardText">{{this.data.tasks_info.processing_tasks}}</h2>
                            </v-skeleton-loader>
                            <p class="infoCardText">Processing tasks</p>
                        </div>
                    </div>
                </div>
                <div id="completedStats" class="col-sm-6 col-md-6 col-lg-4 col-xl-2">
                    <div class="card infoCard">
                        <div class="card-header"><span class="mdi mdi-36px mdi-check-circle-outline"/></div>
                        <div class="card-body infoCardBody">
                            <v-skeleton-loader
                                    :loading="this.loadingStatistics"
                                    transition='scale-transition'
                                    ref="skeleton"
                                    type="image"
                                    class="mx-auto"
                                    height="48px">
                                <h2 class="infoCardText">{{this.data.tasks_info.completed_tasks}}</h2>
                            </v-skeleton-loader>
                            <p class="infoCardText">Completed tasks</p>
                        </div>
                    </div>
                </div>
                <div id="partiallyFailedStats" class="col-sm-6 col-md-6 col-lg-4 col-xl-2">
                    <div class="card infoCard">
                        <div class="card-header"><span class="mdi mdi-36px mdi-comment-question-outline"/></div>
                        <div class="card-body infoCardBody">
                            <v-skeleton-loader
                                    :loading="this.loadingStatistics"
                                    transition='scale-transition'
                                    ref="skeleton"
                                    type="image"
                                    class="mx-auto"
                                    height="48px">
                                <h2 class="infoCardText">{{this.data.tasks_info.partially_failed}}</h2>
                            </v-skeleton-loader>
                            <p class="infoCardText">Partially failed tasks</p>
                        </div>
                    </div>
                </div>
                <div id="failedStats" class="col-sm-6 col-md-6 col-lg-4 col-xl-2">
                    <div class="card infoCard">
                        <div class="card-header"><span class="mdi mdi-36px mdi-alert-rhombus-outline"/></div>
                        <div class="card-body infoCardBody">
                            <v-skeleton-loader
                                    :loading="this.loadingStatistics"
                                    transition='scale-transition'
                                    ref="skeleton"
                                    type="image"
                                    class="mx-auto"
                                    height="48px">
                                <h2 class="infoCardText">{{this.data.tasks_info.failed_tasks}}</h2>
                            </v-skeleton-loader>
                            <p class="infoCardText">Failed tasks</p>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="!isMobile()" id="donutLifetimeStatistics" style="display: flex; align-items: center"
                 class="distributionChart col-12 col-md-4 col-xl-3">
                <v-divider vertical style="display: none" class="m-3 d-md-block"/>
                <v-skeleton-loader
                        :loading="this.loadingStatistics"
                        ref="skeleton"
                        type="image"
                        class="mx-auto"
                        width="325">
                    <apexchart type=donut width="325px" :options="distributionChartOptions"
                               :series="distributionSeries"/>
                </v-skeleton-loader>
            </div>
        </div>

        <br>

        <div class="infoBox">
            <h2>Error statistics</h2>
            <v-spacer/>
            <v-btn large icon @click="this.updateErrorDisplay" :loading="this.loadingErrorInfoCircle">
                <v-icon large :color="sitionColor">mdi-sync</v-icon>
                <template v-slot:loader>
                    <span class="custom-loader">
                        <v-icon :color="sitionColor">mdi-sync</v-icon>
                    </span>
                </template>
            </v-btn>
        </div>
        <hr>
        <div id="errorInfo" class="infoBox flexRow">
            <div id="errorRatio" class="errorStatisticChart col-12 col-xl-3">
                <v-skeleton-loader
                        :loading="this.loadingErrorInfo"
                        transition='scale-transition'
                        ref="skeleton"
                        type="image"
                        class="mx-auto"
                        width="300"
                        height="315">
                    <apexchart width="300px" type="radialBar" :options="errorRatioChartOptions"
                               :series="errorRatioSeries"/>
                </v-skeleton-loader>
            </div>
            <v-divider v-if="!isMobile()" vertical class="errorDivider"/>
            <div id="errorDetails" class="failedTaskTable col-12 col-xl-8">
                <v-skeleton-loader
                        :loading="this.loadingErrorInfo"
                        transition='scale-transition'
                        ref="skeleton"
                        type="table"
                        class="mx-auto"
                        width="100%"
                        height="645">
                    <v-card>
                        <v-card-title>
                            Failed tasks info
                            <v-spacer/>
                            <v-text-field
                                    v-model="errorTableSearch"
                                    append-icon="mdi-table-search"
                                    label="Search"
                                    single-line
                                    hide-details
                            />
                        </v-card-title>
                        <v-data-table
                                :headers="errorTableHeaders"
                                :items="failedTasks"
                                :search="errorTableSearch">
                            <template v-slot:item.action="{ item }">
                                <v-btn icon @click="showTaskDetails(item)"
                                       :loading="loadingTaskDetailsCircle">
                                    <v-icon :color="sitionColor">mdi-card-search-outline</v-icon>
                                    <template v-slot:loader>
                                            <span class="custom-loader">
                                                <v-icon :color="sitionColor">mdi-sync</v-icon>
                                            </span>
                                    </template>
                                </v-btn>
                            </template>
                        </v-data-table>
                    </v-card>
                </v-skeleton-loader>
            </div>
        </div>

        <br>

        <div class="infoBox flexRow">
            <h2>Task history</h2>
            <v-spacer/>
            <v-btn large icon @click="this.updateHistoryDisplay" :loading="this.loadingHistoryCircle">
                <v-icon large :color="sitionColor">mdi-sync</v-icon>
                <template v-slot:loader>
                    <span class="custom-loader">
                        <v-icon :color="sitionColor">mdi-sync</v-icon>
                    </span>
                </template>
            </v-btn>
        </div>
        <hr>
        <div id="history">
            <div class="card-body infoBox">
                <v-skeleton-loader
                        :loading="this.loadingHistory"
                        ref="skeleton"
                        type="image"
                        class="mx-auto"
                        width="100%"
                        height="315">
                    <apexchart height="300" :width="this.historyWidth" type="line" :options="historyChartOptions"
                               :series="historySeries"/>
                </v-skeleton-loader>
            </div>
        </div>

        <v-dialog v-model="taskDetailsPopUp" max-width="70vw" class="taskDetailDialog">
            <v-card>
                <v-toolbar flat style="border-bottom: black 5px">
                    <v-toolbar-title>Details</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn icon @click="taskDetailsPopUp = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-divider style="margin: 0"/>
                <v-tabs vertical :color="sitionColor" class="taskDetailTabs">
                    <v-tab>result</v-tab>
                    <v-tab v-if="accountRole.some(role => ['ROLE_ADMIN', 'ROLE_DEVELOPER'].includes(role))">data</v-tab>
                    <v-tab v-if="accountRole.some(role => ['ROLE_ADMIN', 'ROLE_DEVELOPER'].includes(role))">meta data
                    </v-tab>
                    <v-tab>actions</v-tab>
                    <v-tab-item class="taskDetailTabItem">
                        <v-card flat>
                            <v-card-text>
                                <pre class="dialogText">{{taskDetails.result}}</pre>
                            </v-card-text>
                        </v-card>
                    </v-tab-item>
                    <v-tab-item class="taskDetailTabItem"
                                v-if="accountRole.some(role => ['ROLE_ADMIN', 'ROLE_DEVELOPER'].includes(role))">
                        <v-card flat>
                            <v-card-text>
                                <pre class="dialogText">{{JSON.stringify(taskDetails.data, null, 2)}}</pre>
                            </v-card-text>
                        </v-card>
                    </v-tab-item>
                    <v-tab-item class="taskDetailTabItem"
                                v-if="accountRole.some(role => ['ROLE_ADMIN', 'ROLE_DEVELOPER'].includes(role))">
                        <v-card flat>
                            <v-card-text>
                                <pre class="dialogText">{{JSON.stringify(taskDetails.MetaData, null, 2)}}</pre>
                            </v-card-text>
                        </v-card>
                    </v-tab-item>
                    <v-tab-item class="taskDetailTabItem">
                        <v-card flat>
                            <v-card-text>
                                <h5>This task has been executed: {{taskDetails.runCounter}} time(s)</h5>
                                <v-btn :style="primaryStyle" :disabled="taskDetails.runCounter > 3"
                                       @click="retryTask(taskDetails)">
                                    Retry / add again to queue
                                </v-btn>
                                <div v-if="accountRole.some(role => ['ROLE_ADMIN', 'ROLE_DEVELOPER'].includes(role))">
                                    <v-spacer style="height: 20px"/>
                                    <input type="hidden" id="taskDataForCopy"
                                           :value="JSON.stringify(this.taskDetails.data)">
                                    <v-btn :style="primaryStyle" @click="copyData">Copy the task data</v-btn>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-tab-item>
                </v-tabs>
            </v-card>
        </v-dialog>

    </b-container>
</template>
<style>

</style>
<script>
    import DashboardRepository from "../repositories/dashboard.repository"
    import AccountRepository from "../repositories/account.repository"
    import ProjectRepository from "../repositories/project.repository"
    import TaskRepository from "../repositories/task.repository"

    export default {
        data() {
            return {
                loadingStatistics: true,
                loadingErrorInfo: true,
                loadingHistory: true,
                loadingStatisticsCircle: true,
                loadingErrorInfoCircle: true,
                loadingHistoryCircle: true,
                loadingTaskDetailsCircle: false,
                loadingProjects: false,


                data: DashboardRepository.default(),
                errorTableSearch: '',
                taskDetailsPopUp: false,
                taskDetails: {},

                projects: [{id: -1, name: 'all'},],
                selectedProject: -1,
                accountRole: ['ROLE_USER'],

                distributionSeries: [1, 1, 1, 1, 1],
                distributionChartOptions: {
                    plotOptions: {
                        pie: {
                            donut: {
                                labels: {
                                    show: true,
                                    total: {
                                        show: true
                                    }
                                }
                            }
                        }
                    },
                    chart: {
                        id: 'distributionChart',
                        width: 100
                    },
                    theme: {
                        mode: 'light',
                        palette: 'palette7',
                    },
                    legend: {
                        position: 'bottom'
                    },
                    labels: ['Failed tasks', 'Completed tasks', 'Queued tasks', 'Partially Failed tasks', 'Processing tasks'],
                    responsive: [{
                        breakpoint: 480,
                        options: {
                            chart: {
                                width: 150
                            },
                            legend: {
                                position: 'bottom'
                            }
                        }
                    }]
                },

                errorRatioSeries: [0],
                errorRatioChartOptions: {
                    chart: {
                        id: 'errorRatioChart',
                        offsetY: -10,
                    },
                    theme: {
                        monochrome: {
                            enabled: true,
                            color: '#d91a1a',
                        },
                    },
                    plotOptions: {
                        radialBar: {
                            startAngle: -135,
                            endAngle: 135,
                            dataLabels: {
                                name: {
                                    fontSize: '16px',
                                    color:
                                    undefined,
                                    offsetY:
                                        120
                                },
                                value: {
                                    offsetY: 76,
                                    fontSize: '22px',
                                    color: undefined,
                                    formatter:
                                        function (val) {
                                            return val + "%";
                                        }
                                }
                            }
                        }
                    },
                    fill: {
                        type: 'gradient',
                        gradient: {
                            shade: 'dark',
                            shadeIntensity: 0.15,
                            inverseColors: false,
                            opacityFrom: 1,
                            opacityTo: 1,
                            stops: [0, 50, 65, 91]
                        },
                    },
                    stroke: {
                        dashArray: 3
                    },
                    labels: ['Error ratio']
                },

                errorTableHeaders: [
                    {text: 'Task ID', value: 'id', filterable: false},
                    {text: 'Task Name', value: 'name', filterable: true},
                    {text: 'State', value: 'state', filterable: true},
                    {text: 'Project Name', value: 'project_name', filterable: true},
                    {text: 'Queued at', value: 'queued_at', filterable: true},
                    {text: 'Executed at', value: 'executed_at', filterable: true},
                    {text: 'Details', value: 'action', filterable: false, sortable: false},
                ],
                failedTasks: [],

                historyWidth: 500,
                historyChartOptions: {
                    chart: {
                        id: 'historyChart',
                        toolbar: {
                            show: false
                        },
                        zoom: {
                            enabled: false
                        }
                    },
                    theme: {
                        mode: 'light',
                        palette: 'palette7',
                    },
                    grid: {
                        row: {
                            colors: ['#f3f3f3', 'transparent'], // takes an array which will be repeated on columns
                            opacity: 0.5
                        },
                    },
                    xaxis: {
                        categories: []
                    }
                },
                historySeries: [],
            }
        },
        created() {
            AccountRepository.getUserInfo().then(res => {
                this.accountRole = res.data.role;
                if (this.accountRole.some(role => ['ROLE_ADMIN', 'ROLE_DEVELOPER'].includes(role))) {
                    this.loadingProjects = true;
                    ProjectRepository.getAll().then(res => {
                        this.projects = res.data['hydra:member'].concat([{id: -1, name: 'all'}]);
                        this.loadingProjects = false;
                    }).catch(error => {
                        console.error(error);
                    })
                }
            }).catch(error => {
                console.error(error);
                this.accountRole = ['ROLE_USER'];
            });
            this.updateAllCharts();
            this.updateChartsInterval = setInterval(this.updateAllCharts, 30000);
        },
        beforeRouteLeave: function (to, from, next) {
            clearInterval(this.updateChartsInterval);
            next();
        },
        methods: {
            isMobile() {
                if (/Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
                    return true
                } else {
                    return false
                }
            },
            showTaskDetails(item) {
                this.loadingTaskDetailsCircle = true;
                DashboardRepository.taskDetails(item.id).then(res => {
                    this.taskDetails = res.data;
                    this.taskDetailsPopUp = true;
                    this.loadingTaskDetailsCircle = false;
                });
            },
            retryTask(item) {
                TaskRepository.retryTask(item.id).then(res => {
                    if (res.data.success) {
                        this.$bvToast.toast('The task is succesfully added to the queue.', {
                            title: `Added to queue.`,
                            variant: 'success',
                            solid: true
                        });
                        this.taskDetailsPopUp = false;
                        this.updateErrorDisplay();
                        // success
                    } else {
                        console.error(error);
                    }
                });
            },
            updateAllCharts() {
                if (this.$route.name === "dashboard") {
                    this.updateIndexDisplay();
                    this.updateErrorDisplay();
                    this.updateHistoryDisplay();
                }
            },
            updateIndexDisplay() {
                this.loadingStatisticsCircle = true;
                DashboardRepository.index(this.selectedProject).then(res => {
                    this.data = res.data;
                    this.distributionSeries = [res.data.tasks_info.failed_tasks, res.data.tasks_info.completed_tasks, res.data.tasks_info.queued_tasks, res.data.tasks_info.partially_failed, res.data.tasks_info.processing_tasks];
                    this.loadingStatistics = false;
                    this.loadingStatisticsCircle = false;
                });
            },
            updateErrorDisplay() {
                this.loadingErrorInfoCircle = true;
                DashboardRepository.distribution(this.selectedProject).then(res => {
                    if (this.$route.name === "dashboard") {
                        this.errorRatioSeries = res.data.total !== 0 ? [Math.round(((res.data.failed_tasks.amount * 100) / res.data.total) * 100) / 100] : [0];
                    }
                });
                DashboardRepository.failedTaskInfo(this.selectedProject).then(res => {
                    if (this.$route.name === "dashboard") {
                        this.failedTasks = res.data;
                        this.loadingErrorInfo = false;
                        this.loadingErrorInfoCircle = false;
                    }
                });
            },
            updateHistoryDisplay() {
                this.loadingHistoryCircle = true;
                DashboardRepository.history(this.selectedProject).then(res => {
                    if (this.$route.name === "dashboard") {
                        this.historyWidth = document.getElementById('history').offsetWidth;
                        this.historyChartOptions.xaxis.categories = Object.keys(res.data.completed_tasks);
                        this.historySeries = [{
                            name: 'Failed tasks',
                            data: Object.values(res.data.failed_tasks)
                        }, {
                            name: 'Completed tasks',
                            data: Object.values(res.data.completed_tasks)
                        }];
                        this.loadingHistory = false;
                        this.loadingHistoryCircle = false;
                    }
                });

            },
            copyData() {
                let DataToCopy = document.querySelector('#taskDataForCopy');
                DataToCopy.setAttribute('type', 'text');
                DataToCopy.select();

                try {
                    let successful = document.execCommand('copy');
                    DataToCopy.setAttribute('type', 'hidden');
                    if (successful) {
                        this.$bvToast.toast('The data is succesfully copied to the clipboard.', {
                            title: `Data copied.`,
                            variant: 'success',
                            solid: true
                        });
                    } else {
                        this.$bvToast.toast('Oops, unable to copy, pls. refresh the page and try again.', {
                            title: `Can't copy.`,
                            variant: 'danger',
                            solid: true
                        });
                    }
                } catch (err) {
                    DataToCopy.setAttribute('type', 'hidden');
                    alert('Oops, unable to copy');
                    this.$bvToast.toast('Oops, unable to copy, pls. refresh the page and try again.', {
                        title: `Can't copy.`,
                        variant: 'danger',
                        solid: true
                    });
                }
            }
        },
        computed: {
            projectOptions() {
                return this.projects.map(p => {
                    return {value: p.id, text: p.id + ": " + p.name};
                });
            }
        }
    }
</script>

<style>
    .infoBox {
        display: flex;
        flex-direction: row;
        justify-content: space-around;
    }

    .infoCard {
        min-height: 250px;
    }

    .infoCardBody {
        padding: 20px 10px;
    }

    .infoCardText {
        margin-bottom: 0;
    }

    .distributionChart {
        justify-content: space-around;
    }

    .failedTaskTable {
        width: 80%;
    }

    .errorStatisticChart {
        display: flex;
        align-items: center;
    }

    .errorDivider {
        margin-left: 20px;
        margin-right: 20px;
    }

    .custom-loader {
        animation: loader 2s infinite;
        display: flex;
    }

    @-moz-keyframes loader {
        from {
            transform: rotate(360deg);
        }
        to {
            transform: rotate(0);
        }
    }

    @-webkit-keyframes loader {
        from {
            transform: rotate(360deg);
        }
        to {
            transform: rotate(0);
        }
    }

    @-o-keyframes loader {
        from {
            transform: rotate(360deg);
        }
        to {
            transform: rotate(0);
        }
    }

    @keyframes loader {
        from {
            transform: rotate(360deg);
        }
        to {
            transform: rotate(0);
        }
    }
</style>
